import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useWallet } from "../../../providers/wallet/wallet.provider";
import classNames from "classnames";
import WalletHeaderCards from "../balance/walletHeaderCards";
import WalletList from "../balance/walletList";
import DenyAccess from "../balance/denyAccess";

const BusinessTransactions = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Zustand Wallet State Selectors ─────────────────────────
  const transactionsLoading = useWallet(
    (state) => state.totalTransactionsLoading
  );
  const hasMore = useWallet((state) => state.hasMore);
  const loadMoreTransactions = useWallet(
    (state) => state.loadMoreTotalTransactions
  );
  const totalTransactionsError = useWallet(
    (state) => state.totalTransactionsError
  );
  const organizationCode = useWallet((state) => state.organizationCode);
  const transactions = useWallet((state) => state.totalTransactions);

  // ─── Scroll Position Restoration ─────────────────────────────
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem("walletScrollPosition");
    const previousPath = sessionStorage.getItem("previousPath");

    // Only restore scroll position if we're returning from a contract page
    if (
      savedScrollPosition &&
      location.pathname.includes("/payments") &&
      transactions.length > 0 &&
      !transactionsLoading &&
      previousPath?.includes("/payments/contract/")
    ) {
      const scrollPosition = parseInt(savedScrollPosition, 10);

      // More aggressive delay approach with multiple retries
      const attemptScrollRestore = (attempt = 1) => {
        const container = containerRef.current;

        // Check if container exists and has proper dimensions
        if (
          container &&
          container.scrollHeight > 0 &&
          container.clientHeight > 0
        ) {
          container.scrollTop = scrollPosition;
          return true;
        }

        if (attempt < 8) {
          // Retry with increasing delays: 100ms, 200ms, 500ms, 1000ms, 1500ms, 2000ms, 3000ms, 5000ms
          const delays = [100, 200, 500, 1000, 1500, 2000, 3000, 5000];
          setTimeout(() => {
            attemptScrollRestore(attempt + 1);
          }, delays[attempt - 1]);
        } else {
          console.log("Failed to restore scroll position after all attempts");
        }
        return false;
      };

      // Start with immediate attempt
      attemptScrollRestore();

      // Clear the previous path after restoring scroll
      sessionStorage.removeItem("previousPath");
    }
  }, [location.pathname, transactions.length, transactionsLoading]);

  // ─── Infinite Scroll Handler (Container-level) ──────────────
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when user scrolls near bottom (within 100px)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (!transactionsLoading && hasMore) {
        loadMoreTransactions(organizationCode);
      }
    }
  }, [transactionsLoading, hasMore, loadMoreTransactions, organizationCode]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Try container scroll first
    container.addEventListener("scroll", handleScroll);

    // Also listen to window scroll as fallback
    const handleWindowScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (!transactionsLoading && hasMore) {
          loadMoreTransactions(organizationCode);
        }
      }
    };

    window.addEventListener("scroll", handleWindowScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, [
    handleScroll,
    transactionsLoading,
    hasMore,
    loadMoreTransactions,
    organizationCode,
  ]);

  if (totalTransactionsError === "access_denied") {
    return <DenyAccess />;
  }

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div className={styles["container"]} ref={containerRef}>
      <div
        className={classNames(
          styles["sub_container"],
          styles["manual_withdraw_feature_disabled"]
        )}>
        <h1 className={styles["wallet_page_title"]}>
          {t("lbl_money_transactions")}
        </h1>
        <WalletHeaderCards hideWithdrawCard />
      </div>
      <WalletList containerRef={containerRef} hideWithdrawTab />
    </div>
  );
};

export default BusinessTransactions;
